/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { message } from 'antd';
import { t } from '@aibi-ui/core';
import callApi from './callApi';
import rejectAfterTimeout from './rejectAfterTimeout';
import parseResponse from './parseResponse';
import { CallApi, ClientTimeout, ParseMethod } from '../types';

export default async function callApiAndParseWithTimeout<
  T extends ParseMethod = 'json',
>({
  timeout,
  parseMethod,
  showErrorToast = true,
  ...rest
}: { timeout?: ClientTimeout; parseMethod?: T } & CallApi) {
  const apiPromise = callApi(rest);
  const racedPromise =
    typeof timeout === 'number' && timeout > 0
      ? Promise.race([apiPromise, rejectAfterTimeout<Response>(timeout)])
      : apiPromise;
  const response = await racedPromise;
  if (!response.ok) {
    // 错误处理
    const errorJson = await response.json();
    errorJson.status = errorJson.status ?? response.status;
    if (errorJson.errors) {
      if (showErrorToast) {
        errorJson.errors.forEach((error: any) => {
          message.error(error.message);
        });
      }
      return Promise.reject(errorJson);
    } else if (errorJson.message && typeof errorJson.message === 'object') {
      const text = decodeURI(Object.values(errorJson.message).join('\n'));
      if (showErrorToast) {
        message.error(text);
      }
      errorJson.message = text;
      return Promise.reject(errorJson);
    }
    const text = errorJson.message || errorJson.msg || errorJson.error;
    if (showErrorToast) {
      message.error(t(`${text}`));
    }
    return Promise.reject(errorJson);
  }

  return parseResponse(racedPromise, parseMethod);
}
